import React, { useState, useRef, useMemo } from 'react';
import {
  Image, Palette, Download, Upload, Zap, Minimize2, QrCode, Maximize2, Crop, Key,
  Ruler, Clock, Binary, Droplet, FileCode, Link, FileJson, Type, Activity,
  DollarSign, Percent, TrendingUp, Menu, X, ChevronRight, Search,
  Scissors, FileText, RefreshCw, Eye, SlidersHorizontal, RotateCw, Stamp, Contrast,
  Sparkles, Circle, Pipette, Code, FileImage, ShieldOff,
  Grid3X3, Smile, Frame, LayoutGrid, User, Globe, ImagePlus,
  Hash, AlignLeft, GitCompare, Code2,
  Calendar, Receipt
} from 'lucide-react';
import ImageConverter from './ImageConverter';
import ImageCompressor from './ImageCompressor';
import ImageResizer from './ImageResizer';
import ImageCropper from './ImageCropper';
import QRCodeGenerator from './QRCodeGenerator';
import PasswordGenerator from './PasswordGenerator';
import UnitConverter from './UnitConverter';
import TimestampConverter from './TimestampConverter';
import NumberBaseConverter from './NumberBaseConverter';
import ColorConverter from './ColorConverter';
import Base64Converter from './Base64Converter';
import UrlEncoder from './UrlEncoder';
import JsonYamlConverter from './JsonYamlConverter';
import TextCaseConverter from './TextCaseConverter';
import BMICalculator from './BMICalculator';
import LoanCalculator from './LoanCalculator';
import PercentageCalculator from './PercentageCalculator';
import CompoundInterestCalculator from './CompoundInterestCalculator';
import RemoveBackground from './RemoveBackground';
import ImageOcr from './ImageOcr';
import HeicToJpg from './HeicToJpg';
import WebpConverter from './WebpConverter';
import BlurImage from './BlurImage';
import PhotoFilters from './PhotoFilters';
import RotateFlipImage from './RotateFlipImage';
import ImageWatermark from './ImageWatermark';
import GrayscaleImage from './GrayscaleImage';
import SharpenImage from './SharpenImage';
import RoundImage from './RoundImage';
import ColorPickerFromImage from './ColorPickerFromImage';
import ImageToBase64 from './ImageToBase64';
import SvgToPng from './SvgToPng';
import RemoveExif from './RemoveExif';
import PixelateImage from './PixelateImage';
import SepiaImage from './SepiaImage';
import InvertColors from './InvertColors';
import AddBorder from './AddBorder';
import ImageSplitter from './ImageSplitter';
import ProfilePictureMaker from './ProfilePictureMaker';
import FaviconGenerator from './FaviconGenerator';
import MemeGenerator from './MemeGenerator';
import ImageToIco from './ImageToIco';
import ColorPaletteExtractor from './ColorPaletteExtractor';
import WordCounter from './WordCounter';
import CharacterCounter from './CharacterCounter';
import LoremIpsumGenerator from './LoremIpsumGenerator';
import TextDiffCompare from './TextDiffCompare';
import MarkdownToHtml from './MarkdownToHtml';
import AgeCalculator from './AgeCalculator';
import TipCalculator from './TipCalculator';
import Header from './Header';
import Footer from './Footer';

type Tool = 'compression' | 'conversion' | 'resize' | 'crop' | 'qrcode' | 'password' |
  'unit' | 'timestamp' | 'numberbase' | 'color' | 'base64' | 'url' | 'json' | 'textcase' |
  'bmi' | 'loan' | 'percentage' | 'compound' |
  'removebg' | 'ocr' | 'heictojpg' | 'webpconvert' | 'blur' | 'filters' | 'rotateflip' |
  'watermark' | 'grayscale' | 'sharpen' | 'roundimage' | 'colorpicker' | 'imgtobase64' |
  'svgtopng' | 'removeexif' |
  'pixelate' | 'sepia' | 'invert' | 'addborder' | 'imagesplitter' | 'profilepic' |
  'favicon' | 'meme' | 'imagetoico' | 'colorpalette' |
  'wordcounter' | 'charcounter' | 'loremipsum' | 'textdiff' | 'mdtohtml' |
  'agecalc' | 'tipcalc';

interface ToolDef {
  id: Tool;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  href: string;
}

const ImageToolsApp: React.FC = () => {
  const [activeTool, setActiveTool] = useState<Tool>('compression');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const toolPanelRef = useRef<HTMLDivElement>(null);

  const calculatorTools: ToolDef[] = [
    { id: 'loan', name: 'Loan Calculator', description: 'Mortgage & loan payments', icon: DollarSign, color: 'bg-green-500', href: '/loan-calculator' },
    { id: 'bmi', name: 'BMI Calculator', description: 'Body Mass Index', icon: Activity, color: 'bg-purple-500', href: '/bmi-calculator' },
    { id: 'percentage', name: 'Percentage Calculator', description: 'Quick % calculations', icon: Percent, color: 'bg-amber-500', href: '/percentage-calculator' },
    { id: 'compound', name: 'Compound Interest', description: 'Investment growth', icon: TrendingUp, color: 'bg-indigo-500', href: '/compound-interest-calculator' },
    { id: 'agecalc', name: 'Age Calculator', description: 'Exact age & facts', icon: Calendar, color: 'bg-blue-500', href: '/age-calculator' },
    { id: 'tipcalc', name: 'Tip Calculator', description: 'Tips & bill split', icon: Receipt, color: 'bg-green-500', href: '/tip-calculator' },
  ];

  const imageTools: ToolDef[] = [
    { id: 'compression', name: 'Compress', description: 'Reduce file size', icon: Minimize2, color: 'bg-orange-500', href: '/compress-image' },
    { id: 'resize', name: 'Resize', description: 'Change dimensions', icon: Maximize2, color: 'bg-purple-500', href: '/resize-image' },
    { id: 'crop', name: 'Crop', description: 'Crop images', icon: Crop, color: 'bg-teal-500', href: '/crop-image' },
    { id: 'conversion', name: 'Convert', description: 'Change format', icon: Palette, color: 'bg-green-500', href: '/convert-image' },
    { id: 'removebg', name: 'Remove BG', description: 'Remove background', icon: Scissors, color: 'bg-red-500', href: '/remove-background' },
    { id: 'ocr', name: 'Image to Text', description: 'Extract text (OCR)', icon: FileText, color: 'bg-blue-600', href: '/image-to-text-ocr' },
    { id: 'heictojpg', name: 'HEIC to JPG', description: 'Convert iPhone photos', icon: Image, color: 'bg-pink-500', href: '/heic-to-jpg' },
    { id: 'webpconvert', name: 'WebP Convert', description: 'WebP to PNG/JPG', icon: RefreshCw, color: 'bg-cyan-500', href: '/webp-converter' },
    { id: 'blur', name: 'Blur Image', description: 'Add blur effect', icon: Eye, color: 'bg-indigo-500', href: '/blur-image' },
    { id: 'filters', name: 'Photo Filters', description: 'Adjust colors', icon: SlidersHorizontal, color: 'bg-violet-500', href: '/photo-filters' },
    { id: 'rotateflip', name: 'Rotate & Flip', description: 'Rotate/mirror', icon: RotateCw, color: 'bg-emerald-500', href: '/rotate-flip-image' },
    { id: 'watermark', name: 'Watermark', description: 'Add text overlay', icon: Stamp, color: 'bg-amber-600', href: '/image-watermark' },
    { id: 'grayscale', name: 'Grayscale', description: 'Black & white', icon: Contrast, color: 'bg-gray-500', href: '/grayscale-image' },
    { id: 'sharpen', name: 'Sharpen', description: 'Enhance clarity', icon: Sparkles, color: 'bg-yellow-500', href: '/sharpen-image' },
    { id: 'roundimage', name: 'Round Image', description: 'Circle crop', icon: Circle, color: 'bg-rose-500', href: '/round-image' },
    { id: 'colorpicker', name: 'Color Picker', description: 'Pick from image', icon: Pipette, color: 'bg-fuchsia-500', href: '/color-picker-from-image' },
    { id: 'imgtobase64', name: 'Image to Base64', description: 'Encode image', icon: Code, color: 'bg-slate-500', href: '/image-to-base64' },
    { id: 'svgtopng', name: 'SVG to PNG', description: 'Vector to raster', icon: FileImage, color: 'bg-lime-500', href: '/svg-to-png' },
    { id: 'removeexif', name: 'Remove EXIF', description: 'Strip metadata', icon: ShieldOff, color: 'bg-stone-500', href: '/remove-exif' },
    { id: 'pixelate', name: 'Pixelate', description: 'Pixelate images', icon: Grid3X3, color: 'bg-orange-400', href: '/pixelate-image' },
    { id: 'sepia', name: 'Sepia Filter', description: 'Vintage effect', icon: Palette, color: 'bg-amber-700', href: '/sepia-filter' },
    { id: 'invert', name: 'Invert Colors', description: 'Negate colors', icon: Contrast, color: 'bg-indigo-600', href: '/invert-colors' },
    { id: 'addborder', name: 'Add Border', description: 'Frames & borders', icon: Frame, color: 'bg-teal-600', href: '/add-border' },
    { id: 'imagesplitter', name: 'Image Splitter', description: 'Split into grid', icon: LayoutGrid, color: 'bg-blue-400', href: '/image-splitter' },
    { id: 'profilepic', name: 'Profile Picture', description: 'Social media pics', icon: User, color: 'bg-pink-600', href: '/profile-picture-maker' },
    { id: 'favicon', name: 'Favicon Generator', description: 'Website icons', icon: Globe, color: 'bg-green-600', href: '/favicon-generator' },
    { id: 'meme', name: 'Meme Generator', description: 'Create memes', icon: Smile, color: 'bg-yellow-500', href: '/meme-generator' },
    { id: 'imagetoico', name: 'Image to ICO', description: 'Create icon files', icon: ImagePlus, color: 'bg-violet-600', href: '/image-to-ico' },
    { id: 'colorpalette', name: 'Color Palette', description: 'Extract colors', icon: Palette, color: 'bg-rose-400', href: '/color-palette-extractor' },
  ];

  const utilityTools: ToolDef[] = [
    { id: 'qrcode', name: 'QR Code', description: 'Generate QR codes', icon: QrCode, color: 'bg-blue-500', href: '/qr-code-generator' },
    { id: 'password', name: 'Password', description: 'Strong passwords', icon: Key, color: 'bg-indigo-500', href: '/password-generator' },
  ];

  const converterTools: ToolDef[] = [
    { id: 'unit', name: 'Units', description: 'Unit conversion', icon: Ruler, color: 'bg-cyan-500', href: '/unit-converter' },
    { id: 'timestamp', name: 'Timestamp', description: 'Unix ↔ Date', icon: Clock, color: 'bg-rose-500', href: '/timestamp-converter' },
    { id: 'numberbase', name: 'Number Base', description: 'Bin/Hex/Dec', icon: Binary, color: 'bg-amber-500', href: '/number-base-converter' },
    { id: 'color', name: 'Color', description: 'HEX/RGB/HSL', icon: Droplet, color: 'bg-violet-500', href: '/color-converter' },
    { id: 'base64', name: 'Base64', description: 'Encode/Decode', icon: FileCode, color: 'bg-emerald-500', href: '/base64-converter' },
    { id: 'url', name: 'URL', description: 'URL encoding', icon: Link, color: 'bg-blue-500', href: '/url-encoder' },
    { id: 'json', name: 'JSON/YAML', description: 'Format convert', icon: FileJson, color: 'bg-orange-500', href: '/json-yaml-converter' },
    { id: 'textcase', name: 'Text Case', description: 'Case convert', icon: Type, color: 'bg-pink-500', href: '/text-case-converter' },
  ];

  const textTools: ToolDef[] = [
    { id: 'wordcounter', name: 'Word Counter', description: 'Count words & chars', icon: FileText, color: 'bg-blue-500', href: '/word-counter' },
    { id: 'charcounter', name: 'Char Counter', description: 'Platform limits', icon: Hash, color: 'bg-green-500', href: '/character-counter' },
    { id: 'loremipsum', name: 'Lorem Ipsum', description: 'Placeholder text', icon: AlignLeft, color: 'bg-amber-500', href: '/lorem-ipsum-generator' },
    { id: 'textdiff', name: 'Text Diff', description: 'Compare texts', icon: GitCompare, color: 'bg-red-500', href: '/text-diff' },
    { id: 'mdtohtml', name: 'Markdown to HTML', description: 'MD converter', icon: Code2, color: 'bg-purple-500', href: '/markdown-to-html' },
  ];

  // All tools combined for search
  const allTools = useMemo(() => [
    ...calculatorTools, ...imageTools, ...utilityTools, ...textTools, ...converterTools,
  ], [calculatorTools, imageTools, utilityTools, textTools, converterTools]);

  // Filter tools by search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return null; // null = show normal sections
    const q = searchQuery.toLowerCase();
    const matches = allTools.filter(t =>
      t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
    );
    return matches;
  }, [searchQuery, allTools]);

  const handleToolSelect = (toolId: Tool) => {
    setActiveTool(toolId);
    setSidebarOpen(false);
    // Scroll to tool panel on mobile
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        toolPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const renderActiveTool = () => {
    switch (activeTool) {
      case 'compression': return <ImageCompressor />;
      case 'resize': return <ImageResizer />;
      case 'crop': return <ImageCropper />;
      case 'conversion': return <ImageConverter />;
      case 'qrcode': return <QRCodeGenerator />;
      case 'password': return <PasswordGenerator />;
      case 'unit': return <UnitConverter />;
      case 'timestamp': return <TimestampConverter />;
      case 'numberbase': return <NumberBaseConverter />;
      case 'color': return <ColorConverter />;
      case 'base64': return <Base64Converter />;
      case 'url': return <UrlEncoder />;
      case 'json': return <JsonYamlConverter />;
      case 'textcase': return <TextCaseConverter />;
      case 'bmi': return <BMICalculator />;
      case 'loan': return <LoanCalculator />;
      case 'percentage': return <PercentageCalculator />;
      case 'compound': return <CompoundInterestCalculator />;
      case 'removebg': return <RemoveBackground />;
      case 'ocr': return <ImageOcr />;
      case 'heictojpg': return <HeicToJpg />;
      case 'webpconvert': return <WebpConverter />;
      case 'blur': return <BlurImage />;
      case 'filters': return <PhotoFilters />;
      case 'rotateflip': return <RotateFlipImage />;
      case 'watermark': return <ImageWatermark />;
      case 'grayscale': return <GrayscaleImage />;
      case 'sharpen': return <SharpenImage />;
      case 'roundimage': return <RoundImage />;
      case 'colorpicker': return <ColorPickerFromImage />;
      case 'imgtobase64': return <ImageToBase64 />;
      case 'svgtopng': return <SvgToPng />;
      case 'removeexif': return <RemoveExif />;
      case 'pixelate': return <PixelateImage />;
      case 'sepia': return <SepiaImage />;
      case 'invert': return <InvertColors />;
      case 'addborder': return <AddBorder />;
      case 'imagesplitter': return <ImageSplitter />;
      case 'profilepic': return <ProfilePictureMaker />;
      case 'favicon': return <FaviconGenerator />;
      case 'meme': return <MemeGenerator />;
      case 'imagetoico': return <ImageToIco />;
      case 'colorpalette': return <ColorPaletteExtractor />;
      case 'wordcounter': return <WordCounter />;
      case 'charcounter': return <CharacterCounter />;
      case 'loremipsum': return <LoremIpsumGenerator />;
      case 'textdiff': return <TextDiffCompare />;
      case 'mdtohtml': return <MarkdownToHtml />;
      case 'agecalc': return <AgeCalculator />;
      case 'tipcalc': return <TipCalculator />;
      default: return <ImageCompressor />;
    }
  };

  const ToolButton: React.FC<{ tool: ToolDef; compact?: boolean }> = ({ tool, compact }) => {
    const Icon = tool.icon;
    const isActive = activeTool === tool.id;

    return (
      <a
        href={tool.href}
        onClick={(e) => {
          e.preventDefault();
          handleToolSelect(tool.id);
        }}
        className={`block w-full text-left p-3 rounded-lg transition-all duration-200 no-underline ${
          isActive
            ? 'bg-primary-100 border-2 border-primary-500'
            : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${tool.color} flex-shrink-0`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <a href={tool.href} className="no-underline">
              <p className={`font-medium text-sm truncate ${isActive ? 'text-primary-900' : 'text-gray-900'}`}>
                {tool.name}
              </p>
            </a>
            {!compact && (
              <p className={`text-xs truncate ${isActive ? 'text-primary-600' : 'text-gray-500'}`}>
                {tool.description}
              </p>
            )}
          </div>
          {isActive && <ChevronRight className="w-4 h-4 text-primary-500 flex-shrink-0" />}
        </div>
      </a>
    );
  };

  const ToolSection: React.FC<{ title: string; tools: ToolDef[]; compact?: boolean }> = ({ title, tools, compact }) => (
    <div className="mb-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">{title}</h3>
      <div className="space-y-1">
        {tools.map((tool) => <ToolButton key={tool.id} tool={tool} compact={compact} />)}
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      <Header />

      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main layout — fills remaining height, no page scroll */}
      <div className="flex-1 flex min-h-0">
        {/* Sidebar — scrolls internally */}
        <aside className={`
          lg:w-72 lg:flex-shrink-0
          fixed lg:static inset-y-0 left-0 z-40
          w-80 max-w-[85vw]
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          bg-gray-50 lg:bg-gray-50
          flex flex-col
          pt-16 lg:pt-0
          border-r border-gray-200
        `}>
          {/* Fixed header + search */}
          <div className="flex-shrink-0 bg-gradient-to-br from-blue-600 to-blue-700 p-4 text-white lg:rounded-tr-none">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="font-bold text-base">A</span>
              </div>
              <div>
                <h1 className="font-bold text-base">AllTools</h1>
                <p className="text-blue-200 text-[10px]">{allTools.length} free online tools</p>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-300" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tools..."
                className="w-full pl-8 pr-8 py-1.5 bg-white/15 border border-white/20 rounded-lg text-white placeholder-blue-200 text-xs focus:outline-none focus:bg-white/25 focus:border-white/40"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-blue-200 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Scrollable tool list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1 pb-20 lg:pb-3">
            {filteredSections ? (
              <div>
                <p className="text-[10px] text-gray-500 px-1 mb-2">
                  {filteredSections.length} result{filteredSections.length !== 1 ? 's' : ''} for "{searchQuery}"
                </p>
                {filteredSections.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No tools found</p>
                ) : (
                  <div className="space-y-1">
                    {filteredSections.map((tool) => <ToolButton key={tool.id} tool={tool} compact />)}
                  </div>
                )}
              </div>
            ) : (
              <>
                <ToolSection title={`Image Tools (${imageTools.length})`} tools={imageTools} compact />
                <ToolSection title={`Calculators (${calculatorTools.length})`} tools={calculatorTools} compact />
                <ToolSection title={`Text Tools (${textTools.length})`} tools={textTools} compact />
                <ToolSection title={`Utilities (${utilityTools.length})`} tools={utilityTools} compact />
                <ToolSection title={`Converters (${converterTools.length})`} tools={converterTools} compact />
              </>
            )}
          </div>
        </aside>

        {/* Main Content — scrolls internally */}
        <div ref={toolPanelRef} className="flex-1 min-w-0 overflow-y-auto p-4 lg:p-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
            {renderActiveTool()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageToolsApp;
